import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fdate } from "@/lib/fmt";
import { PageHeader, Field } from "@/components/Common";
import { ImportDialog } from "@/components/ImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { name: form.name, email: form.email, role: form.role, phone: form.phone || "", password: form.password || null };
      if (form.id) await api.put(`/users/${form.id}`, body); else await api.post("/users", body);
      toast.success("User tersimpan");
      setForm(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try { await api.delete(`/users/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/users/${id}/status`, { status }); toast.success(status === "active" ? "User disetujui" : "Pendaftaran ditolak"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pending = rows.filter((u) => u.status === "pending").length;

  return (
    <div>
      <PageHeader title="Users" subtitle="Kelola akun sales & admin. Sales hanya dapat melihat datanya sendiri.">
        <ImportDialog entity="users" title="Users" onDone={load} />
        <Button onClick={() => setForm({ name: "", email: "", password: "", role: "sales", phone: "" })} data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1" />Tambah User</Button>
      </PageHeader>
      {pending > 0 && <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" data-testid="users-pending-alert"><b>{pending}</b> pendaftaran baru menunggu persetujuan.</div>}
      <div className="card-premium overflow-x-auto fade-up">
        <table className="tbl w-full" data-testid="users-table">
          <thead><tr><th>Nama</th><th>Email</th><th>Role</th><th>Status</th><th>No. HP</th><th>Dibuat</th><th /></tr></thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={u.id} data-testid={`user-row-${i}`}>
                <td className="font-medium">{u.name}</td><td className="text-sm">{u.email}</td>
                <td><Badge variant="outline" className={u.role === "admin" ? "bg-blue-50 text-blue-800" : ""}>{u.role}</Badge></td>
                <td>
                  {(u.status || "active") === "active" && <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Aktif</Badge>}
                  {u.status === "pending" && <Badge variant="outline" className="bg-amber-50 text-amber-700">Menunggu</Badge>}
                  {u.status === "rejected" && <Badge variant="outline" className="bg-rose-50 text-rose-700">Ditolak</Badge>}
                </td>
                <td className="text-sm">{u.phone}</td><td className="text-sm">{fdate(u.created_at)}</td>
                <td className="whitespace-nowrap">
                  {u.status === "pending" && (
                    <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-1" onClick={() => setStatus(u.id, "active")} data-testid={`user-approve-${i}`}><Check className="h-4 w-4 mr-1" />Setujui</Button>
                      <Button size="sm" variant="outline" className="bg-white mr-1" onClick={() => setStatus(u.id, "rejected")} data-testid={`user-reject-${i}`}><X className="h-4 w-4 mr-1" />Tolak</Button>
                    </>
                  )}
                  {u.status === "rejected" && <Button size="sm" variant="outline" className="bg-white mr-1" onClick={() => setStatus(u.id, "active")} data-testid={`user-activate-${i}`}>Aktifkan</Button>}
                  <Button size="icon" variant="ghost" onClick={() => setForm({ ...u, password: "" })} data-testid={`user-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(u.id)} data-testid={`user-delete-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{form?.id ? "Edit" : "Tambah"} User</DialogTitle></DialogHeader>
          {form && (
            <div className="grid gap-4">
              <Field label="Nama Sales"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" /></Field>
              <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" /></Field>
              <Field label={form.id ? "Password baru (kosongkan jika tidak diubah)" : "Password"}><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" /></Field>
              <Field label="No. HP"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-form-phone" /></Field>
              <Field label="Role"><select className="h-10 rounded-md border px-3 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-form-role"><option value="sales">Sales</option><option value="teknisi">Teknisi</option><option value="admin">Admin</option></select></Field>
              <Button onClick={save} data-testid="user-save-btn">Simpan</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
